import { EmploymentType } from "../../../../utils/models";
import type { HiringGapsGrid, HiringGridCoord, HiringSuggestedHire } from "../types";

/** Days per schedule week — the grid's cell-key stride, shared with the backend's `_GRID_DAYS_PER_WEEK`. */
export const DAYS_PER_WEEK = 7;

export const VALID_SHIFT_LENGTHS = [8, 12] as const;
export type ShiftLength = (typeof VALID_SHIFT_LENGTHS)[number];

/** A person's week, editable. The wire shape plus the user's decisions (`applied`) and their edits. */
export type SuggestedHireDraft = {
	/** The server's `suggested-N` for a generated row, or `manual-{n}` for one the user added. */
	id: string;
	/**
	 * A name the USER owns — set by renaming the row, or by adding a hire manually. Absent on a
	 * generated row, whose name is derived from the shape of its week on every render (see
	 * `displayNames`). Once set it is never recomputed, whatever the user changes afterwards.
	 */
	customName?: string;
	/** Cell key (`weekIdx * 7 + dayIdx`) → shift label. The person's whole week; `days` is its key set. */
	shiftByDay: Record<number, string>;
	/** Shift labels this person can be scheduled into, chronological. Never empty. */
	coverShifts: string[];
	/**
	 * The real length in whole hours of the template this person covers — displayed as the row's
	 * `8H` / `12H` pill, and re-derived from the response's `templateHours` whenever the user re-points
	 * the row at other templates (see `setHireShifts` for the multi-template rule). NOT the SHIFT LENGTH
	 * setting, and nothing is derived from it: the employment badge is a shift count over the
	 * SHIFTS / WEEK setting.
	 */
	shiftLength: number;
	/** Injected into the Recurring row as a client-side overlay. Never written anywhere. */
	applied: boolean;
};

export const cellKey = (weekIdx: number, dayIdx: number): number => weekIdx * DAYS_PER_WEEK + dayIdx;
export const weekIdxOf = (key: number): number => Math.floor(key / DAYS_PER_WEEK);
export const dayIdxOf = (key: number): number => key % DAYS_PER_WEEK;

/**
 * Shifts that make one full-time week at a given shift length. Mirrors the backend's
 * `_SHIFTS_PER_FTE_8H` / `_12H`, and like it, this is ONLY the default for the `SHIFTS / WEEK` setting
 * (what the backend derives when the caller sends no override) — never the employment badge's
 * denominator. The badge divides by the setting the user typed; deriving it from the 8h/12h button was
 * the DAT-676 defect.
 */
const SHIFTS_PER_FTE: Record<number, number> = { 8: 5, 12: 3 };
export const shiftsPerFte = (shiftLength: number): number => SHIFTS_PER_FTE[shiftLength] ?? SHIFTS_PER_FTE[8];

/**
 * Shift label → that template's real length in whole hours. The response's `templateHours`, verbatim.
 * Display only — it feeds the row's `8H` / `12H` pill, never the employment badge.
 */
export type TemplateHours = Record<string, number>;

/**
 * The shift lengths the SHIFT LENGTH control may offer this department: the ones its own templates run,
 * in `VALID_SHIFT_LENGTHS` order.
 *
 * A department on an all-8h roster has nothing to choose, so the caller hides the control rather than
 * showing a `12h` segment that describes no shift it has. A template on some other length (10h, 8.5h)
 * contributes no option — `SHIFTS_PER_FTE` only models 8 and 12, so offering one would be a segment
 * whose full-time week we cannot state.
 */
export const availableShiftLengths = (templateHours: TemplateHours): ShiftLength[] => {
	const present = new Set(Object.values(templateHours));
	return VALID_SHIFT_LENGTHS.filter((hours) => present.has(hours));
};

/** FTE-ratio thresholds for the derived badge. Mirrors the backend's `_employment_type_for_ratio`. */
const FULL_TIME_RATIO = 0.8;
const PART_TIME_RATIO = 0.2;

/**
 * The badge is DERIVED, never targeted: the packer fills the gap and this reports what came out. Recomputed
 * on every edit so a row that loses days stops claiming to be full time.
 */
export const employmentTypeForRatio = (ratio: number): EmploymentType => {
	if (ratio >= FULL_TIME_RATIO) {
		return EmploymentType.FullTime;
	}
	return ratio >= PART_TIME_RATIO ? EmploymentType.PartTime : EmploymentType.PRN;
};

/**
 * Averaged over the WHOLE master-schedule cycle, not the busiest week — the cycle repeats, so someone
 * working 5 shifts in week 0 and none in week 1 is genuinely half an FTE.
 */
export const averageShiftsPerWeek = (draft: SuggestedHireDraft, gridWeeks: number): number =>
	gridWeeks > 0 ? Object.keys(draft.shiftByDay).length / gridWeeks : 0;

/**
 * Shifts in the person's template over `SHIFTS / WEEK (PER PERSON)` — the number the user typed in
 * Settings, per the PRD. NOT `shiftsPerFte(draft.shiftLength)`: the two are equal by default, so that
 * read correctly until someone set the box to 4 while the button said 12h and was measured against 3.
 *
 * MUST stay in step with the backend's `_build_suggested_hires`: this re-derives the badge with no round
 * trip when a day is toggled, so a drift shows up as the badge disagreeing with the server on first
 * paint. Same convention as `computeMissing` / `_build_recommendation_diff_grid`.
 */
export const employmentTypeOf = (draft: SuggestedHireDraft, gridWeeks: number, shiftsPerWeek: number): EmploymentType =>
	employmentTypeForRatio(averageShiftsPerWeek(draft, gridWeeks) / shiftsPerWeek);

/** Shifts per displayed week, in week order — the row's "4-4 per wk" readout. */
export const shiftsByWeek = (draft: SuggestedHireDraft, gridWeeks: number): number[] => {
	const counts = Array.from({ length: gridWeeks }, () => 0);
	for (const key of Object.keys(draft.shiftByDay)) {
		const weekIdx = weekIdxOf(Number(key));
		if (weekIdx >= 0 && weekIdx < gridWeeks) {
			counts[weekIdx] += 1;
		}
	}
	return counts;
};

/**
 * `shiftByDay` keys arrive as JSON strings (JSON has no integer keys), so they are re-keyed to numbers
 * here — the one place the wire shape is normalised, exactly like the grids' inner keys.
 */
export const toDraft = (hire: HiringSuggestedHire): SuggestedHireDraft => {
	const shiftByDay: Record<number, string> = {};
	for (const [key, label] of Object.entries(hire.shiftByDay)) {
		shiftByDay[Number(key)] = label;
	}
	// No `customName`: a generated row's name is derived, so it follows the row as the user edits it.
	return { id: hire.id, shiftByDay, coverShifts: [...hire.coverShifts], shiftLength: hire.shiftLength, applied: false };
};

/**
 * The applied hires as a grid in the same shape as `actualGrid`, so the table can add it to Recurring at
 * render time. Nothing here mutates the schedule template — this module is read-only and stays that way.
 */
export const buildAppliedGrid = (drafts: SuggestedHireDraft[]): HiringGapsGrid => {
	const grid: HiringGapsGrid = {};
	for (const draft of drafts) {
		if (!draft.applied) {
			continue;
		}
		for (const [key, label] of Object.entries(draft.shiftByDay)) {
			const row = grid[label] ?? {};
			row[Number(key)] = (row[Number(key)] ?? 0) + 1;
			grid[label] = row;
		}
	}
	return grid;
};

/**
 * The PRD's employment mix: "Recommendations should be 70% Full time, 20% Part time and 10% PRN."
 *
 * A TARGET THAT IS REPORTED, NEVER ENFORCED — confirmed with Product. Nothing in the packer aims at
 * these numbers, and deliberately so: the only ways to hit a ratio are to invent a hire nobody needs
 * or to leave a gap open, and both are worse than being off-target. The mix a department gets is a
 * readout of the shape of its gap against `SHIFTS / WEEK` — so it lives here, next to the mix it is
 * compared with, and reaches the user as a tooltip on the summary chips.
 */
export const TARGET_EMPLOYMENT_MIX: Record<SuggestedEmploymentType, number> = {
	[EmploymentType.FullTime]: 0.7,
	[EmploymentType.PartTime]: 0.2,
	[EmploymentType.PRN]: 0.1,
};

/**
 * The three types a suggestion can carry. `EmploymentType` also has `travel`, `perDiem`, `floatPool`
 * and `unknown`, which describe how somebody is ENGAGED — they are never derived from an FTE ratio, so
 * `employmentTypeForRatio` cannot return one and the mix never has a cell for it.
 */
export type SuggestedEmploymentType = EmploymentType.FullTime | EmploymentType.PartTime | EmploymentType.PRN;

const SUGGESTED_EMPLOYMENT_TYPES: SuggestedEmploymentType[] = [EmploymentType.FullTime, EmploymentType.PartTime, EmploymentType.PRN];

/** This type's share of the suggested people, as a whole percent. No people is 0%, not a division by zero. */
export const mixSharePercent = (mix: Record<EmploymentType, number>, type: SuggestedEmploymentType): number => {
	const total = SUGGESTED_EMPLOYMENT_TYPES.reduce((sum, key) => sum + mix[key], 0);
	return total === 0 ? 0 : Math.round((mix[type] / total) * 100);
};

/** Per-employment-type headcount for the summary band. Zero-filled so the three cells always render. */
export const employmentMix = (drafts: SuggestedHireDraft[], gridWeeks: number, shiftsPerWeek: number): Record<EmploymentType, number> => {
	const mix = { [EmploymentType.FullTime]: 0, [EmploymentType.PartTime]: 0, [EmploymentType.PRN]: 0 } as Record<EmploymentType, number>;
	for (const draft of drafts) {
		const type = employmentTypeOf(draft, gridWeeks, shiftsPerWeek);
		mix[type] = (mix[type] ?? 0) + 1;
	}
	return mix;
};

/** Hides the rows whose every shift type the user switched off in Settings — "I'm not hiring for that shift". */
export const isHireVisible = (draft: SuggestedHireDraft, disabledShifts: readonly string[]): boolean =>
	draft.coverShifts.some((label) => !disabledShifts.includes(label));

/**
 * How many shift labels the pill spells out before it starts counting.
 *
 * ONE. Two labels joined — "8AM-4PM + 4PM-12AM" — is about twice the width the identity column budgets
 * for this pill, and the chips after it are fixed-width facts that do not yield, so the overflow came
 * out of them: the `8H` shift length rendered UNDERNEATH the pill's own text and the `4-5 PER WK`
 * count fell off the end entirely.
 *
 * Two was tried first, on the theory that two is all the generator can emit
 * (`_MAX_SHIFT_TYPES_PER_HIRE`) so an unedited row should read in full. It does not fit, and clipping
 * it with an ellipsis only trades one unreadable pill for another. Collapsing at one puts the pill
 * back to the single-label width the column was measured against, at every selection size — and the
 * full list is still on the row twice over: in the derived NAME beside it, and in the picker this
 * pill opens.
 */
const COLLAPSED_SHIFT_LABELS = 1;

/**
 * The pill's text: the first label spelled out, then `+N` for the rest.
 */
export const shiftPillLabel = (coverShifts: string[]): string => {
	const hidden = coverShifts.length - COLLAPSED_SHIFT_LABELS;
	const shown = coverShifts.slice(0, COLLAPSED_SHIFT_LABELS).join(" + ");
	return hidden > 0 ? `${shown} +${hidden}` : shown;
};

/**
 * The grid cells a hire's row points at — one per day they work, at THAT day's shift.
 *
 * It used to be the cross product of their days against every label in `coverShifts`, so hovering a
 * two-shift person boxed both shift rows on every working day and half the highlighted cells named a
 * shift they were not on that day. `shiftByDay` IS the assignment, so it answers this exactly, and the
 * same map is what `buildAppliedGrid` counts — the preview and the Apply now describe the same cells.
 *
 * Shared with `HiringModule` rather than re-derived there: a highlight that disagrees with the overlay
 * is the shape of the original bug.
 */
export const hireGridCoords = (draft: SuggestedHireDraft): HiringGridCoord[] =>
	Object.entries(draft.shiftByDay).map(([key, shiftLabel]) => ({
		weekIdx: weekIdxOf(Number(key)),
		dayIdx: dayIdxOf(Number(key)),
		shiftLabel,
	}));

/**
 * Shift labels a saved row still points at that the department no longer runs.
 *
 * A label is not stored anywhere — it is `format_shift_label(shift_start, shift_end)`, computed from
 * whatever hours the schedule happens to have. So it stops existing the moment those hours change:
 * move nights from `11pm-7am` to `7pm-7am` and the old label matches nothing, while a row saved in
 * this browser still names it. Saved rows deliberately outlive a refetch (that is what protects a
 * rename), so the row never learns.
 *
 * The damage is silent: Apply adds coverage to a grid row that is not drawn, so the button appears to
 * work and no number moves. Hence ANY unknown label makes the row stale — a partly-valid row would
 * still mis-count the half that is gone.
 *
 * Returns the dead labels rather than a boolean so the row can name them in the reason it shows.
 */
export const retiredShiftsOf = (draft: SuggestedHireDraft, knownLabels: readonly string[]): string[] => {
	const known = new Set(knownLabels);
	const referenced = new Set([...draft.coverShifts, ...Object.values(draft.shiftByDay)]);
	return [...referenced].filter((label) => !known.has(label));
};

/** Keeps `coverShifts` in the template's chronological order however the user picks them. */
export const sortByTemplateOrder = (labels: string[], templateOrder: readonly string[]): string[] =>
	[...labels].sort((a, b) => templateOrder.indexOf(a) - templateOrder.indexOf(b));

const WEEKEND_DAY_INDICES = new Set([0, 6]);

/**
 * A generated row's name, from the shape of its week: "Full Time", "Weekend", "Weekdays", or the shift
 * label plus its type.
 *
 * DERIVED on every render rather than stored, because the name makes a claim the row can outgrow. It
 * used to be baked in by the backend at generation time, so raising `SHIFTS / WEEK` left a person still
 * called "Part Time" while their badge said PRN — the name asserting something the rest of the row
 * contradicted. Now it moves with the employment type it names, from the same `employmentTypeOf`.
 *
 * It is also why the strings live here and not in the service: they are user-facing copy and now go
 * through `t()`, where the backend's literals ("Full Time") were English on every locale.
 */
export const suggestedHireBaseName = (draft: SuggestedHireDraft, employmentType: EmploymentType, t: NameTranslate): string => {
	const dayIndices = Object.keys(draft.shiftByDay).map((key) => dayIdxOf(Number(key)));
	if (employmentType === EmploymentType.FullTime) {
		return t("hiring.hire.name.fullTime");
	}
	// An empty week (a manual row nobody has given days yet) is not "weekdays only" — `every` on an
	// empty list is true, which would have named it after a shape it does not have.
	if (dayIndices.length > 0 && dayIndices.every((dayIdx) => WEEKEND_DAY_INDICES.has(dayIdx))) {
		return t("hiring.hire.name.weekend");
	}
	if (dayIndices.length > 0 && !dayIndices.some((dayIdx) => WEEKEND_DAY_INDICES.has(dayIdx))) {
		return t("hiring.hire.name.weekdays");
	}
	// Straddles the weekend (or has no days at all), so the week's shape says nothing: lead with the shift.
	return t("hiring.hire.name.shiftType", {
		shift: draft.coverShifts.join(" + "),
		type: employmentType === EmploymentType.PartTime ? t("employment.type.part.time") : t("employment.type.prn"),
	});
};

type NameTranslate = (key: string, options?: Record<string, unknown>) => string;

/**
 * The name each row renders, in list order.
 *
 * A row the user named keeps that name untouched — no numbering, no re-derivation, ever. The rest are
 * derived and then numbered only where a base name repeats, so a one-of-a-kind "Weekend" stays bare.
 * Numbering counts the derived names ONLY: a user's own "Weekend" must not push the generated one to
 * "Weekend 2", because that would be their edit renaming somebody else's row.
 */
export const displayNames = (drafts: SuggestedHireDraft[], gridWeeks: number, shiftsPerWeek: number, t: NameTranslate): string[] => {
	const derived = drafts.map((draft) =>
		draft.customName === undefined ? suggestedHireBaseName(draft, employmentTypeOf(draft, gridWeeks, shiftsPerWeek), t) : null,
	);
	const totals = new Map<string, number>();
	for (const base of derived) {
		if (base !== null) {
			totals.set(base, (totals.get(base) ?? 0) + 1);
		}
	}
	const seen = new Map<string, number>();
	return drafts.map((draft, idx) => {
		const base = derived[idx];
		if (base === null) {
			return draft.customName as string;
		}
		if ((totals.get(base) ?? 0) === 1) {
			return base;
		}
		const nth = (seen.get(base) ?? 0) + 1;
		seen.set(base, nth);
		return t("hiring.hire.name.numbered", { name: base, index: nth });
	});
};
